import { useEffect, useRef, useState } from 'react';
import { CopyFindingList } from '../../components/CopyFindingList';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { PLATFORM_CHAR_LIMITS, countCharacters, platformLabel } from '../../lib/platforms';
import type { DraftVariantRead, PlatformName } from '../../types/api';
import { updateVariantCaption } from '../generation/generationApi';
import { useCopyCheck } from '../knowledge-base/useCopyCheck';

/**
 * Hard cap on a caption, mirroring `MAX_CAPTION_LENGTH` on the backend: the
 * widest platform limit. The per-platform limit is the copy check's job.
 */
const CAPTION_MAX_LENGTH = Math.max(...Object.values(PLATFORM_CHAR_LIMITS));

/** Faster than the knowledge base's bench: this text is already written. */
const CHECK_DEBOUNCE_MS = 300;

/** Character share at which the count starts warning. */
const NEAR_LIMIT_RATIO = 0.9;

interface CaptionEditorProps {
  businessId: string;
  variant: DraftVariantRead;
  /** The request's platform — what the character limit is measured against. */
  platform: PlatformName;
  /** A decision is in flight elsewhere on the screen; editing waits its turn. */
  isDisabled?: boolean;
  /** True when this caption's fact-check is on screen beneath the editor. */
  hasFactCheckNearby?: boolean;
  /** The saved variant, for the page to put back in place of the old one. */
  onSaved: (variant: DraftVariantRead) => void;
  /** The request moved on without us; reload it. */
  onStale?: () => void;
}

function countClassName(count: number, limit: number): string {
  if (count > limit) return 'caption-editor__count caption-editor__count--over';
  if (count >= limit * NEAR_LIMIT_RATIO) return 'caption-editor__count caption-editor__count--near';
  return 'caption-editor__count';
}

/** Message for the domain errors a caption edit can provoke. */
function captionMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      // The guardrail's own sentence, listing what it objected to. Ours would
      // only be vaguer.
      case 'CAPTION_REJECTED':
        return caught.message;
      case 'REQUEST_NOT_EDITABLE':
        return 'This post has moved past editing. Reloading it now.';
      case 'VARIANT_NOT_SELECTED':
        return 'Only the caption you chose can be edited at this step.';
      case 'VARIANT_NOT_FOUND':
        return 'That version is no longer here. Reload the post and try again.';
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That edit did not save. Try again.');
}

/**
 * The user's own words, over the model's.
 *
 * A caption is text, so it stays editable at both review steps: either variant
 * while copy selection is open, the chosen one at final approval. The edit is
 * free and deterministic — like a layer edit it never calls a model and never
 * touches the monthly quota — and it runs live against the same copy guardrails
 * the pipeline uses (`/copy-check`), so whatever would stop a save is on screen
 * before it is attempted. `warn` findings are shown and block nothing; `fail`
 * findings block the save, because the server would refuse it anyway (422
 * `CAPTION_REJECTED`) and saying so twice helps nobody.
 *
 * A hand-edited caption is deliberately **not** re-fact-checked: that would be
 * a model call on a free action. So when this variant carries an edit and its
 * verdicts are on screen, the editor says outright that those verdicts describe
 * the text as it was generated.
 */
export function CaptionEditor({
  businessId,
  variant,
  platform,
  isDisabled = false,
  hasFactCheckNearby = false,
  onSaved,
  onStale,
}: CaptionEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(variant.caption);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Empty text while closed: there is nothing to check about a caption nobody
  // is editing, and the closed editor must not put a call on the wire.
  const check = useCopyCheck(businessId, platform, isEditing ? draft : '', CHECK_DEBOUNCE_MS);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const count = countCharacters(draft);
  const limit = PLATFORM_CHAR_LIMITS[platform];
  const trimmed = draft.trim();
  const isEmpty = trimmed.length === 0;
  const isUnchanged = trimmed === variant.caption.trim();
  const failures = check.result?.findings.filter((finding) => finding.severity === 'fail') ?? [];
  const canSave = !isSaving && !isEmpty && !isUnchanged && failures.length === 0;

  function startEditing() {
    setDraft(variant.caption);
    setError(null);
    setIsSaved(false);
    setIsEditing(true);
  }

  function cancelEditing() {
    if (isSaving) return;
    setIsEditing(false);
    setDraft(variant.caption);
    setError(null);
  }

  async function handleSave() {
    if (!canSave) return;

    setError(null);
    setIsSaving(true);
    try {
      const saved = await updateVariantCaption(businessId, variant.request_id, variant.id, {
        caption: trimmed,
      });
      setIsEditing(false);
      setIsSaving(false);
      setIsSaved(true);
      onSaved(saved);
    } catch (caught) {
      setError(captionMessage(caught));
      setIsSaving(false);
      if (caught instanceof ApiError && caught.code === 'REQUEST_NOT_EDITABLE') onStale?.();
    }
  }

  const editedNote =
    variant.caption_edited_at === null ? null : (
      <p className="caption-editor__edited">
        Edited after generation.
        {hasFactCheckNearby ? ' The fact check below reflects the original text.' : null}
      </p>
    );

  if (!isEditing) {
    return (
      <div className="caption-editor">
        <p className="caption-editor__text">{variant.caption}</p>
        {editedNote}
        <div className="caption-editor__bar">
          <button
            className="button button--ghost caption-editor__open"
            type="button"
            disabled={isDisabled}
            onClick={startEditing}
          >
            Edit caption
          </button>
          {isSaved ? (
            <p className="caption-editor__receipt" role="status">
              Caption updated
            </p>
          ) : null}
        </div>
        {/* A save only fails with the editor closed when the page moved on
            under it, so the message stays put rather than vanishing. */}
        {error ? (
          <p className="form__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="caption-editor caption-editor--editing">
      {editedNote}

      <label className="field">
        <span className="field__label">Caption</span>
        <textarea
          className="field__input caption-editor__input"
          ref={inputRef}
          rows={8}
          maxLength={CAPTION_MAX_LENGTH}
          value={draft}
          disabled={isSaving}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') cancelEditing();
          }}
        />
        <span className="field__hint">
          Your words replace the generated ones. Nothing is regenerated and no quota is spent —
          Escape cancels.
        </span>
      </label>

      <p className="caption-editor__meter">
        <span className={countClassName(count, limit)}>
          {count}/{limit}
        </span>
        <span className="caption-editor__meter-label">characters on {platformLabel(platform)}</span>
      </p>

      {check.error ? (
        <p className="caption-editor__check-error" role="status">
          {check.error} You can still save — we run the caption check again when you do.
        </p>
      ) : null}

      {check.isChecking ? <p className="kb-section__status">Running the caption check</p> : null}

      {check.result && check.result.findings.length === 0 && !check.isChecking ? (
        <p className="kb-section__status">Caption check — length and words to avoid all OK.</p>
      ) : null}

      <CopyFindingList findings={check.result?.findings ?? []} />

      {failures.length > 0 ? (
        <p className="caption-editor__blocked">
          {failures.length === 1
            ? 'One caption check above fails. Fix it to save — we can’t save this text as it stands.'
            : `${failures.length} caption checks above fail. Fix them to save — we can’t save this text as it stands.`}
        </p>
      ) : null}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="caption-editor__bar">
        <button
          className="button button--primary"
          type="button"
          disabled={!canSave}
          onClick={() => void handleSave()}
        >
          {isSaving ? 'Saving' : 'Save caption'}
        </button>
        <button className="button" type="button" disabled={isSaving} onClick={cancelEditing}>
          Cancel
        </button>
        {isEmpty ? (
          <p className="caption-editor__blocked">A caption cannot be empty.</p>
        ) : isUnchanged ? (
          <p className="caption-editor__hint">Nothing changed yet.</p>
        ) : null}
      </div>
    </div>
  );
}
